<template>
  <b-row class="flow-chart">
    <b-col>
      <dashboard-flow-chart :nodes="nodes" />
    </b-col>
  </b-row>
</template>

<script>
import DashboardFlowChart from '@/components/Dashboard/DashboardFlowChart';
import StatCircle from '@/components/Dashboard/StatCircle';
import StatBox from '@/components/Dashboard/StatBox';
import { mapGetters } from 'vuex';
import ActionStatus from '@/services/Action/ActionStatus';
import DashboardHierarchyMixin from '@/components/Dashboard/DashboardHierarchyMixin';
import canSeePendingApprovalTab from '@/services/Action/ActionPermissions';
import { PERMISSION_TYPE_ACTION } from '@/config/permission';

export default {
  name: "ActionCountHierarchy",
  components: {
    DashboardFlowChart,
  },
  props: {
    total: {
      type: Number,
    }
  },
  mixins: [DashboardHierarchyMixin],
  data() {
    return {
      nodes: [
        {
          id: "1",
          parentId: -1,
          nodeComponent: StatCircle,
          data: {
            value: 0,
            bgColor: "#F2F5F8",
            icon: 'clientactions',
            label: 'OPEN',
            labelClass: 'text-dark',
            to: {
              name: 'actions',
            },
          },
        },
        this.getAllPendingApproval(),
        {
          id: "3",
          parentId: "1",
          nodeComponent: StatCircle,
          data: {
            value: 0,
            color: '#FFFFFF',
            bgColor: "#00D2B4",
            label: 'QUERIES',
            to: {
              name: 'actionStatus',
              params: { statusFilter: ActionStatus.getRouteByStatus('query') },
            },
          },
        },
        {
          id: "4",
          parentId: "1",
          nodeComponent: StatCircle,
          data: {
            value: 0,
            color: '#FFFFFF',
            bgColor: "#C388BB",
            label: 'NO ACCESS',
            to: {
              name: 'actionStatus',
              params: { statusFilter: ActionStatus.getRouteByStatus('no access') },
            },
          },
        },
        {
          id: "5",
          parentId: "1",
          nodeComponent: StatCircle,
          data: {
            value: 0,
            color: '#FFFFFF',
            bgColor: "#638BC2",
            label: 'ASSIGNED & UNASSIGNED',
            to: {
              name: 'actions',
              query: { status: [
                  ActionStatus.ASSIGNED(),
                  ActionStatus.UNASSIGNED(),
                  ActionStatus.getLabelForSecondaryOrCompletionStatus(ActionStatus.NO_ACCESS_RESPONSE()),
                  ActionStatus.getLabelForSecondaryOrCompletionStatus(ActionStatus.QUERY_RESPONSE()),
                  ActionStatus.getLabelForSecondaryOrCompletionStatus(ActionStatus.REJECTED()),
                  ActionStatus.getLabelForSecondaryOrCompletionStatus(ActionStatus.PARTIAL())
                ]
              },
              params: { title: 'Assigned & Unassigned' },
            },
          },
        },
        {
          id: "assigned",
          parentId: "5",
          nodeComponent: StatBox,
          data: {
            value: 0,
            bgColor: "#F2F5F8",
            color: '#638BC2',
            label: 'ASSIGNED',
            labelColor: '#2C3E38',
            align: "center",
            size: "x-small",
            cssClass: 'py-1 stat-box',
            to: {
              name: 'actions',
              query: { status: ActionStatus.ASSIGNED() },
            },
          },
        },
        {
          id: "unassigned",
          parentId: "assigned",
          nodeComponent: StatBox,
          data: {
            value: 0,
            bgColor: "#F2F5F8",
            color: '#638BC2',
            label: 'UNASSIGNED',
            labelColor: '#2C3E38',
            align: "center",
            size: "x-small",
            cssClass: 'py-1 stat-box',
            to: {
              name: 'actionStatus',
              params: { statusFilter: ActionStatus.getRouteByStatus('unassigned') },
            },
          },
        },


        {
          id: "rejected",
          parentId: "unassigned",
          nodeComponent: StatBox,
          data: {
            value: 0,
            bgColor: "#F2F5F8",
            color: '#638BC2',
            label: 'REJECTED',
            labelColor: '#2C3E38',
            align: "center",
            size: "x-small",
            cssClass: 'py-1 stat-box',
            to: {
              name: 'actions',
              query: { status: ActionStatus.getLabelForSecondaryOrCompletionStatus(ActionStatus.REJECTED()) },
            },
          },
        },
        {
          id: "partial",
          parentId: "rejected",
          nodeComponent: StatBox,
          data: {
            value: 0,
            label: 'PARTIALLY COMPLETED',
            bgColor: "#F2F5F8",
            color: '#638BC2',
            labelColor: '#2C3E38',
            align: "center",
            size: "x-small",
            cssClass: 'py-1 stat-box',
            to: {
              name: 'actions',
              query: { status: ActionStatus.getLabelForSecondaryOrCompletionStatus(ActionStatus.PARTIAL()) },
            },
          },
        },

        {
          id: "query response",
          parentId: "partial",
          nodeComponent: StatBox,
          data: {
            value: 0,
            label: 'RESPONDED TO QUERY',
            bgColor: "#F2F5F8",
            color: '#638BC2',
            labelColor: '#2C3E38',
            align: "center",
            size: "x-small",
            cssClass: 'py-1 stat-box',
            to: {
              name: 'actions',
              query: { status: ActionStatus.getLabelForSecondaryOrCompletionStatus(ActionStatus.QUERY_RESPONSE()) },
            },
          },
        },
        {
          id: "no access response",
          parentId: "query response",
          nodeComponent: StatBox,
          data: {
            value: 0,
            label: 'RESPONDED TO NO ACCESS',
            bgColor: "#F2F5F8",
            color: '#638BC2',
            labelColor: '#2C3E38',
            align: "center",
            size: "x-small",
            cssClass: 'py-1 stat-box',
            to: {
              name: 'actions',
              query: { status: ActionStatus.getLabelForSecondaryOrCompletionStatus(ActionStatus.NO_ACCESS_RESPONSE()) },
            },
          },
        },
      ],
    };
  },
  computed: {
    ...mapGetters([
      'counts',
    ]),
  },
  created() {
    this.updateCounts();
  },
  methods: {
    updateCounts() {
      // Level 1
      this.nodes[0].data.value = this.total;

      // Level 2
      this.nodes[1].data.value = this.counts['pending approval unfiltered'];
      this.nodes[2].data.value = this.counts['query'];
      this.nodes[3].data.value = this.counts['no access'];
      this.nodes[4].data.value = this.counts['assigned']
          + this.counts['unassigned']
          + this.counts['rejected']
          + this.counts['partial']
          + this.counts['query response']
          + this.counts['no access response'];

      // Level 3
      this.updateValue('assigned', this.counts['assigned']);
      this.updateValue('unassigned', this.counts['unassigned']);
      this.updateValue('rejected', this.counts['rejected']);
      this.updateValue('partial', this.counts['partial']);
      this.updateValue('query response', this.counts['query response']);
      this.updateValue('no access response', this.counts['no access response']);

      this.filterHierarchy('assigned');
    },
    getAllPendingApproval() {
      const can = permission => this.$can(permission, PERMISSION_TYPE_ACTION);
      const canSeeAllPendingApproval = canSeePendingApprovalTab(can);

      const navItem = {
        id: '2',
        parentId: '1',
        nodeComponent: StatCircle,
        data: {
          value: 0,
          color: '#FFFFFF',
          bgColor: '#FFA600',
          label: 'ALL PENDING APPROVAL',
        },
      };

      if (canSeeAllPendingApproval) {
        navItem.data.to = {
          name: 'actionStatus',
          params: {
            statusFilter: ActionStatus.getRouteByStatus('pending approval'),
          },
        };
      } else {
        navItem.data.to = {
          name: 'actions',
          query: {
            status: 'pending approval',
          },
        };
      }

      return navItem;
    },
  }
}
</script>

<style scoped>

</style>
